import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Skeleton } from '@material-ui/lab';
import { TEACHERS_HOME_URL } from 'config/urls';
import { coachingStatuses, coachingTabs } from 'constants/ui';
import {
  useUserClassroomMisconceptions,
  userClassroomMisconceptionsGenerate,
  userClassroomMisconceptionsWarmup
} from 'sdk';

import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { notifyError } from 'utils/notifications';

import UserContext from 'components/UserContext';

import styles from './styles.module.scss';

const CoachingContent = ({ setMisconceptionsLastUpdated }) => {
  const [isFailed, setIsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(coachingTabs.WEEKLY);
  const [startPolling, setStartPolling] = useState(false);
  const [skippedTabs, setSkippedTabs] = useState({});
  const [tick, setTick] = useState(0);

  const processingTabRef = useRef(null);
  const isGeneratingRef = useRef(false);

  const { user } = useContext(UserContext);

  const {
    data: userMisconceptionsMovesData,
    isLoading: isUserClassroomMisconceptionsLoading,
    refetch: refetchUserClassroomMisconceptions
  } = useUserClassroomMisconceptions({
    params: { period: selectedTab.toLowerCase() }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const cooldownPeriodPassed = useMemo(() => {
    if (tick < 0) return false;
    const lastFetchTime = userMisconceptionsMovesData?.created_at;
    if (isUserClassroomMisconceptionsLoading) return false;
    if (!lastFetchTime) return true;

    const twelveMinutes = 12 * 60 * 1000;

    return (
      new Date().getTime() - new Date(lastFetchTime).getTime() > twelveMinutes
    );
  }, [
    userMisconceptionsMovesData?.created_at,
    isUserClassroomMisconceptionsLoading,
    tick
  ]);

  const triggerInsightGeneration = useCallback(
    async (tab) => {
      if (processingTabRef.current === tab) return;
      if (isGeneratingRef.current) return;

      processingTabRef.current = tab;
      isGeneratingRef.current = true;
      setIsLoading(true);
      setIsFailed(false);

      try {
        const response = await userClassroomMisconceptionsGenerate({
          period: tab.toLowerCase()
        });

        if (response?.data.status === coachingStatuses.SKIPPED) {
          setSkippedTabs((prev) => ({ ...prev, [tab.toLowerCase()]: true }));
          setStartPolling(false);
          setIsLoading(false);
          return;
        }

        setSkippedTabs((prev) => ({ ...prev, [tab.toLowerCase()]: false }));

        refetchUserClassroomMisconceptions();
        setStartPolling(true);
      } catch (error) {
        setIsFailed(true);
        notifyError('Could not generate new Insights data.');
        processingTabRef.current = null;
      } finally {
        setIsLoading(false);
        isGeneratingRef.current = false;
      }
    },
    [refetchUserClassroomMisconceptions]
  );

  useEffect(() => {
    if (userMisconceptionsMovesData?.created_at) {
      setMisconceptionsLastUpdated(userMisconceptionsMovesData.created_at);
    }
  }, [userMisconceptionsMovesData, setMisconceptionsLastUpdated]);

  useEffect(() => {
    if (isUserClassroomMisconceptionsLoading) return;

    const isGenerationIdle =
      userMisconceptionsMovesData?.generation_in_progress === false;

    if (
      (cooldownPeriodPassed === true ||
        userMisconceptionsMovesData?.has_insights_generation_error === true) &&
      isGenerationIdle
    ) {
      triggerInsightGeneration(selectedTab);
    }
  }, [
    cooldownPeriodPassed,
    userMisconceptionsMovesData?.generation_in_progress,
    userMisconceptionsMovesData?.has_insights_generation_error,
    isUserClassroomMisconceptionsLoading,
    selectedTab,
    triggerInsightGeneration
  ]);

  useEffect(() => {
    if (userMisconceptionsMovesData?.generation_in_progress === true) {
      setStartPolling(true);
    } else {
      setStartPolling(false);
    }
  }, [userMisconceptionsMovesData?.generation_in_progress]);

  useEffect(() => {
    if (startPolling) {
      const interval = setInterval(() => {
        refetchUserClassroomMisconceptions();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [startPolling, refetchUserClassroomMisconceptions]);

  useEffect(() => {
    userClassroomMisconceptionsWarmup();
  }, []);

  const isTabSkipped = skippedTabs[selectedTab.toLowerCase()];

  const showSkeletons =
    (isLoading || isUserClassroomMisconceptionsLoading || startPolling) &&
    (!userMisconceptionsMovesData?.misconceptions ||
      userMisconceptionsMovesData.misconceptions.length === 0);

  const isEmpty =
    !showSkeletons &&
    !startPolling &&
    (isTabSkipped ||
      userMisconceptionsMovesData?.misconceptions?.length === 0 ||
      userMisconceptionsMovesData?.period !== selectedTab.toLowerCase());

  const showGlobalError =
    isFailed ||
    (!startPolling &&
      !isLoading &&
      userMisconceptionsMovesData?.has_insights_generation_error &&
      userMisconceptionsMovesData?.misconceptions?.length === 0);

  const handleTabChange = ({ tab }) => {
    setSelectedTab(tab);

    trackMixpanelEvent(user.id, 'discuss_in_plc_coaching', {
      feature: 'discuss in plc / coaching',
      view: 'home page',
      button: tab.toLowerCase(),
      normalizedURL: process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_HOME_URL
    });
  };

  return (
    <div className={styles.coachingWidget}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${
            selectedTab === coachingTabs.DAILY ? styles.active : ''
          }`}
          onClick={() => handleTabChange({ tab: coachingTabs.DAILY })}
        >
          Daily
        </button>
        <button
          className={`${styles.tab} ${
            selectedTab === coachingTabs.WEEKLY ? styles.active : ''
          }`}
          onClick={() => handleTabChange({ tab: coachingTabs.WEEKLY })}
        >
          Weekly
        </button>
        <button
          className={`${styles.tab} ${
            selectedTab === coachingTabs.MONTHLY ? styles.active : ''
          }`}
          onClick={() => handleTabChange({ tab: coachingTabs.MONTHLY })}
        >
          Monthly
        </button>
      </div>

      <div className={styles.cardList}>
        {showSkeletons ? (
          <div className={styles.loadingState}>
            <Skeleton variant="rect" width="100%" height={104} />
            <Skeleton variant="rect" width="100%" height={104} />
          </div>
        ) : showGlobalError ? (
          <div className={styles.errorState}>
            We encountered an issue while generating insights for your classroom
            misconception. Please try again later.
          </div>
        ) : isEmpty ? (
          <div className={styles.noInsightsContainer}>
            <p className={styles.noInsightsTitle}>
              No insights for the {selectedTab.toLowerCase()} period
            </p>
          </div>
        ) : (
          userMisconceptionsMovesData?.misconceptions?.map((item, index) => (
            <div key={item.name || index} className={styles.card}>
              <div className={styles.cardHeader}>
                <h5 className={styles.cardTitle}>{item.name}</h5>
              </div>
              <p className={styles.description}>{item.description}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CoachingContent;
